import type { StationDetail } from "@/types/mobility";

/**
 * 지점 위치 한 줄 — "일본 도쿄도 시나가와구" (09-23).
 *
 * 지점 카드에서 이름 **위**에 올리는 줄이다. 찾아오는 길("고탄다역에서 도보 1분")만으로는
 * 처음 보는 사람이 그 동네가 어느 나라·어느 도시인지 알 수 없다.
 *
 * - 국가명은 ISO 코드(`JP`)를 `Intl.DisplayNames` 로 현재 언어에 맞게 푼다.
 * - 한·중·일은 큰 단위부터(국가 → 광역 → 시·구), 영어는 주소 관례대로 작은 단위부터
 *   쉼표로 잇는다("Shinagawa, Tokyo, Japan").
 * - 광역과 시·구가 같으면(드묾) 한 번만 쓴다. 비어 있는 칸은 건너뛴다.
 * - 일본 시골 주소의 군(郡)은 뗀다(09-29) — 캠핑장은 "미나미츠루군 후지카와구치코초"처럼
 *   군+정이 `city` 한 칸에 들어 있어 줄이 잘렸다. 군은 실생활에서 거의 안 쓰는 단위다.
 */
export function formatStationLocation(
  detail: Pick<StationDetail, "country" | "stateProvince" | "city">,
  locale: string,
): string {
  const country = countryName(detail.country, locale);
  const state = detail.stateProvince?.trim() || null;
  const rawCity = detail.city?.trim() || null;
  const city =
    rawCity && detail.country?.trim().toUpperCase() === "JP"
      ? withoutJapaneseCounty(rawCity)
      : rawCity;
  const parts = [country, state, city && city !== state ? city : null].filter(
    (part): part is string => !!part,
  );
  return locale.startsWith("en") ? [...parts].reverse().join(", ") : parts.join(" ");
}

/**
 * "미나미츠루군 후지카와구치코초" → "후지카와구치코초", "南都留郡富士河口湖町" → "富士河口湖町",
 * "Fujikawaguchiko Town, Minamitsuru District" → "Fujikawaguchiko Town".
 * 군 뒤에 정·촌이 없으면(군만 있으면) 그대로 둔다.
 */
export function withoutJapaneseCounty(city: string): string {
  const en = city
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (en.length > 1) {
    const kept = en.filter((part) => !/(\sDistrict|-gun)$/i.test(part));
    if (kept.length > 0 && kept.length < en.length) return kept.join(", ");
  }
  const stripped = city.replace(/^\S+군\s+(?=\S)/, "").replace(/^[^郡]+郡(?=.)/, "");
  return stripped || city;
}

function countryName(code: string | null, locale: string): string | null {
  const value = code?.trim();
  if (!value) return null;
  if (!/^[A-Za-z]{2}$/.test(value)) return value;
  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(value.toUpperCase()) ??
      value
    );
  } catch {
    return value;
  }
}
